import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFeatureFlagEnabled } from "@sunrun/sales-experience-shared/react-feature-flags";
import {
  getSplatUserType,
  isRetailUser,
} from "@sunrun/sales-experience-shared";
import { RETAIL_RECORDING_FEATURE_FLAG } from "../constants";
import { useRetailRecording } from "../hooks/useRetailRecording";
import { RecordingFloatingIndicator } from "./RecordingFloatingIndicator";
import { StartRecordingModal } from "./StartRecordingModal";
import { getNewPath } from "adapters/common";
import { useUserProfile } from "providers/UserProfile";

// Top-level orchestrator: gates the whole feature behind the
// sales-experience-retail-recording flag, then renders either the blocking "start
// recording" modal or the persistent floating indicator depending on the
// current recording status.
const RetailRecordingGate = () => {
  const { userProfile } = useUserProfile();
  const isFeatureEnabled = useFeatureFlagEnabled(RETAIL_RECORDING_FEATURE_FLAG);
  const isRetailRep = userProfile
    ? isRetailUser(getSplatUserType(userProfile))
    : false;

  const isActive = isFeatureEnabled && isRetailRep;

  return isActive ? (
    <ActiveRetailRecording contactId={userProfile.contactId} />
  ) : null;
};

const ActiveRetailRecording = ({ contactId }: { contactId: string }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCreateCustomerRoute = pathname === "/customers/create";
  const {
    status,
    errorMessage,
    elapsedSeconds,
    startRecording,
    stopRecording,
    dismissRecordingPrompt,
    requireRecordingPrompt,
  } = useRetailRecording({ contactId });

  useEffect(() => {
    if (isCreateCustomerRoute && status === "dismissed") {
      requireRecordingPrompt();
    }
  }, [isCreateCustomerRoute, status, requireRecordingPrompt]);

  const handleGoBackToDashboard = useCallback(() => {
    dismissRecordingPrompt();
    navigate(getNewPath("/dashboard"));
  }, [dismissRecordingPrompt, navigate]);

  if (status === "checking" || status === "dismissed") {
    return null;
  }

  if (status === "prompting" || status === "starting" || status === "error") {
    return (
      <StartRecordingModal
        status={status}
        errorMessage={errorMessage}
        onStart={startRecording}
        onNotInStore={dismissRecordingPrompt}
        showNotInStoreAction={!isCreateCustomerRoute && status === "prompting"}
        onGoBackToDashboard={handleGoBackToDashboard}
        showGoBackToDashboardAction={isCreateCustomerRoute}
      />
    );
  }

  return (
    <RecordingFloatingIndicator
      status={status}
      elapsedSeconds={elapsedSeconds}
      onStop={stopRecording}
    />
  );
};

export { RetailRecordingGate };
